import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { clientLabel, pickableClients, type ClientOption } from "@/lib/clients";
import { newLine, type ShareLine } from "@/lib/shareLines";

/** Desktop columns of a share line: customer, amount, margin, invoice no., remove. */
const SHARE_COLS = "sm:grid-cols-[minmax(0,1fr)_6rem_4.5rem_7.5rem_2.25rem]";

/** Customer share lines (customer, amount or Rest, margin, Rechnungs-Nr.), shared by the classify and rule dialogs. */
export function ShareLinesEditor({ lines, onChange, clients, defaultMarginPct, refPlaceholder = "RE-Nr." }: {
  lines: ShareLine[];
  onChange: (update: (lines: ShareLine[]) => ShareLine[]) => void;
  /** Includes former clients; a line offers them only when it already points at one. */
  clients: ClientOption[];
  defaultMarginPct: number;
  refPlaceholder?: string;
}) {
  const setLine = (key: string, patch: Partial<ShareLine>) => onChange((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  return (
    <>
      <div className={`hidden gap-2 text-xs text-muted-foreground sm:grid ${SHARE_COLS}`}>
        <span>Kunde</span><span>Anteil €</span><span>Marge %</span><span>Rechnungs-Nr.</span><span />
      </div>
      {lines.map((l) => (
        // Phone: customer + remove on the first line, the three numbers below (columns headed by placeholders).
        <div key={l.key} className={`grid grid-cols-6 gap-2 ${SHARE_COLS}`}>
          <div className="col-span-5 min-w-0 sm:col-span-1">
            <Select value={l.clientId === null ? "" : String(l.clientId)} onValueChange={(v) => setLine(l.key, { clientId: Number(v) })}>
              <SelectTrigger aria-label="Kunde" className="w-full min-w-0"><SelectValue placeholder="Kunde" /></SelectTrigger>
              <SelectContent>{pickableClients(clients, [l.clientId]).map((c) => <SelectItem key={c.id} value={String(c.id)}>{clientLabel(c)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button type="button" variant="ghost" size="icon" aria-label="Zeile entfernen" className="sm:order-last" disabled={lines.length === 1} onClick={() => onChange((ls) => ls.filter((x) => x.key !== l.key))}>
            <X className="size-4" />
          </Button>
          <Input aria-label="Anteil €" className="col-span-2 sm:col-span-1" inputMode="decimal" placeholder="Rest" value={l.amountText} onChange={(e) => setLine(l.key, { amountText: e.target.value })} />
          <Input aria-label="Marge %" className="col-span-2 sm:col-span-1" inputMode="decimal" placeholder={`${defaultMarginPct} %`} value={l.marginText} onChange={(e) => setLine(l.key, { marginText: e.target.value })} />
          <Input aria-label="Rechnungs-Nr." className="col-span-2 sm:col-span-1" placeholder={refPlaceholder} value={l.rebillRef} onChange={(e) => setLine(l.key, { rebillRef: e.target.value })} />
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" onClick={() => onChange((ls) => [...ls, newLine()])}>+ Kunde</Button>
    </>
  );
}
